import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Modal,
  Text,
  Title,
  Spinner,
} from '@cochlear-design-system/foundation';
import { withFeature } from 'flagged';
import {
  routes,
  envUrlSRPortal,
  ACCESS_TOKEN_COOKIE,
  ID_TOKEN_COOKIE,
  envUrl,
} from '../../config';
import providerService from '../../services/providerService';
import cookieService from '../../services/cookieService';
import getLocalStorageLabels from '../../utils/getLocalStorageLabels';
import { setIdpParam } from '../../utils/authprovider';
import { ScheduleSRPortal } from '../ScheduleSRPortal';
import { shouldUseAmplify } from '../../utils/apiTarget';
import { setClinicSynced } from '../../utils/mcpClinicSync';

const ProgrammingFileRequests = ({ locale, country, lang }) => {
  const labels = getLocalStorageLabels(locale);
  const history = useHistory();
  const location = useLocation();
  const pDetails = providerService.getProviderDetails();
  const chosenProvider = pDetails.providerId;
  const cochlearId = pDetails.providerList.providers.filter(
    (provider) => provider.id === chosenProvider,
  )[0]?.id;

  // Get the current provider to check for the useNewSRPortal flag
  const currentProvider = providerService.getCurrentProvider();

  const useModuleFederationPFR = shouldUseAmplify(
    currentProvider?.useNewSRPortal ?? false,
  );

  const [srUrl, setSrUrl] = useState('');
  const [showModalPFR, setShowModalPFR] = useState(false);
  const [pfrLoaded, setPfrLoaded] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showSelected, setShowSelected] = useState(false);

  const viewPrfIframe = useRef();
  const viewPrfListIframe = useRef();
  const [modalDivHeight, setModalDivHeight] = useState('1vh');

  // For Module Federation approach
  const accessToken = cookieService.getCookie(ACCESS_TOKEN_COOKIE);
  const idToken = cookieService.getCookie(ID_TOKEN_COOKIE);
  const { providerId } = providerService.getProviderDetails();
  const logoutUrl = `${envUrl}${routes.dpx.logout}`;

  const programmingFilesConfig = {
    enabled: true,
    scheduleSRPortalScriptPath: `${envUrlSRPortal}/static/chunks/remoteEntry.js`,
    remoteModulePath: 'srPortal/SRPortalApp',
    apiBaseUrl: envUrlSRPortal,
  };

  useEffect(() => {
    // Only set up postMessage listener for legacy MCP approach
    if (useModuleFederationPFR) return undefined;

    const handleMessage = (e) => {
      if (e.origin !== routes.mcp.domain) return;
      if (typeof e.data === 'object') {
        if ('modalEvent' in e.data) {
          switch (e.data.modalEvent.target) {
            case 'programmingFile':
              if (e.data.modalEvent.type === 'open') {
                setSrUrl(e.data.modalEvent.payload.url);
                setShowModalPFR(true);
                setPfrLoaded(false);
                setShowSelected(false);
              }
              break;
            default:
              break;
          }
        }
        if ('pageEvent' in e.data) {
          const mcpPageHeight = e.data.pageEvent.payload?.height;
          switch (e.data.pageEvent.type) {
            case 'pageLoaded':
              if (
                e.data.pageEvent.target === 'selectedServiceRequest'
              ) {
                if (viewPrfIframe && viewPrfIframe.current) {
                  setShowSelected(true);
                  setPfrLoaded(true);
                  viewPrfIframe.current.style.height =
                    mcpPageHeight === 0
                      ? '1800px'
                      : `${mcpPageHeight + 100}px`;
                }
              }
              if (e.data.pageEvent.target === 'programmingFileList') {
                // MCP has switched the active clinic context. Record it so
                // other MCP-iframe pages can tell the clinic is synced for this
                // browser session and skip redirecting home.
                setClinicSynced(cochlearId);
                setShowList(true);
                // less than 200 means that the page is collapsed, but MCP may send correct height as well if user clicks "Refresh" button
                viewPrfListIframe.current.style.height =
                  mcpPageHeight < 200
                    ? '450px'
                    : `${mcpPageHeight}px`;
                if (mcpPageHeight < 200) {
                  setModalDivHeight('60vh');
                } else {
                  setModalDivHeight(`${mcpPageHeight}px`);
                }
              }
              break;

            default:
              break;
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [useModuleFederationPFR]);

  const handleSRClick = (serviceRequestId, patientId, csrId) => {
    history.push({
      pathname: `/${country}/${lang}/${routes.dpx.viewServiceRequest}`,
      state: {
        serviceRequestId,
        patientId,
        csrId,
        returnPath: `${location.pathname}${location.search}`,
        source: 'programming-file-requests',
      },
    });
  };

  // Render Module Federation component for regions with flag enabled
  if (useModuleFederationPFR) {
    return (
      <ScheduleSRPortal
        config={programmingFilesConfig}
        accessToken={accessToken}
        idToken={idToken}
        selectedProviderId={providerId}
        pageType="ProgrammingRequests"
        logoutUrl={logoutUrl}
        hideInternalNavigation
        onSRClick={handleSRClick}
      />
    );
  }

  // Legacy MCP iframe approach for other regions
  return (
    <div style={{ height: modalDivHeight }}>
      <Modal
        footer=""
        show={showModalPFR}
        title=""
        onHide={() => setShowModalPFR(false)}
        size="xl"
      >
        <Modal.Header
          closeButton
          data-analytics="close_view_sr_modal"
        >
          <Modal.Title>&nbsp;</Modal.Title>
        </Modal.Header>
        <Title
          content={
            labels['labels.programmingFileRequests.modalTitle']
          }
          size="heading-2"
          tag="h2"
        />

        {!pfrLoaded && (
          <div
            style={{
              margin: 'auto',
              textAlign: 'center',
              marginTop: '3rem',
            }}
          >
            <>
              <Spinner animation="border" />
              <Text
                content={
                  labels['labels.programmingFileRequests.loading']
                }
              />
            </>
          </div>
        )}
        <iframe
          src={setIdpParam(`${routes.mcp.domain}${srUrl}&dpx=true`)}
          title="View PFR"
          width="100%"
          ref={viewPrfIframe}
          style={{
            marginTop: '2rem',
            visibility: showSelected ? 'visible' : 'hidden',
          }}
        />
      </Modal>

      <iframe
        src={setIdpParam(
          `${routes.mcp.programmingFileRequests}${cochlearId}`,
        )}
        id="modaliFrame-programmingFileSR"
        title="programmingFileList"
        width="100%"
        ref={viewPrfListIframe}
        style={{
          visibility: showList ? 'visible' : 'hidden',
        }}
      />
    </div>
  );
};

export default withFeature('featureEnabler/programFileRequests')(
  ProgrammingFileRequests,
);
